import classNames from 'classnames';
import { format } from 'date-fns/format';
import { formatISO } from 'date-fns/formatISO';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { KeyboardEvent, SyntheticEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import type { DropdownItemProps, DropdownProps } from 'semantic-ui-react';
import { Button, Dropdown, Input } from 'semantic-ui-react';
import { useOnClickOutside } from 'usehooks-ts';

import { RELATIVE_DATE_PRESET_OPTIONS } from '@models/date.const';
import { parseLocalCalendarDate } from '@utils/date';
import { getLocale } from '@utils/locale';
import { getRelativeDateRange, isCustomDateRange } from '@utils/relative-date';

import s from './date-range.scss';

export const dateRangePresets = {
  ...RELATIVE_DATE_PRESET_OPTIONS,
  custom: {
    label: 'Custom Range',
  },
} as const;

export type DateRangePresetValue = keyof typeof dateRangePresets;

export type DateRangeValue =
  Exclude<DateRangePresetValue, 'custom'> | readonly [string, string | null];

export type DateRangeRawValues = readonly [Date | null, Date | null];

export interface DateRangeProps extends Omit<
  DropdownProps,
  'onChange' | 'value'
> {
  className?: string;
  datePickerClassName?: string;
  /**
   * Callback when the user selects a preset or applies a custom range. Raw date
   * values are always included for convenience.
   */
  onChange: (value: DateRangeValue, rawValues: DateRangeRawValues) => void;
  /**
   * Either a relative preset or an array of date strings for a custom range.
   */
  value?: DateRangeValue;
}

const formatDate = (date: Date | null) =>
  date ? format(date, 'P', { locale: getLocale() }) : 'N/A';

const getDropdownValue = (
  value: DateRangeValue | undefined,
): DateRangePresetValue | undefined => {
  if (!value) return undefined;

  return isCustomDateRange(value) ? 'custom' : value;
};

/**
 * Renders a dropdown list of preset relative date ranges, as well as a custom
 * date range picker for full control.
 */
export const DateRange = ({
  className,
  datePickerClassName,
  disabled,
  onChange,
  value: initialValue,
  ...dropdownProps
}: DateRangeProps) => {
  useStyles(s);

  const startDateInputRef = useRef<HTMLInputElement>(null);
  const endDateInputRef = useRef<HTMLInputElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  const formatStartDateInput = (date: Date | null) => {
    if (!startDateInputRef.current) return;
    startDateInputRef.current.value = formatDate(date);
  };

  const formatEndDateInput = (date: Date | null) => {
    if (!endDateInputRef.current) return;
    endDateInputRef.current.value = formatDate(date);
  };

  // Last emitted value, or the initial prop if defined
  // Used to initialize/restore state where necessary
  const [value, setValue] = useState<DateRangeValue | undefined>(initialValue);

  // Dropdown state is controlled
  const [isOpen, setIsOpen] = useState(false);

  const [dropdownValue, setDropdownValue] = useState<
    DateRangePresetValue | undefined
  >(() =>
    // Set custom mode if the value passed in is an array of date strings
    getDropdownValue(value),
  );

  // Custom range inputs
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(null);

  /**
   * Tracks when a change has been made but not emitted, enabling state to be
   * reset if the user does not apply the changes. The control becomes dirty
   * when the user selects the Custom Range option, or an existing custom range
   * is modified.
   */
  const [isDirty, setIsDirty] = useState(false);

  /**
   * The selected dropdown option is conditionally reset when it is closed
   * without applying changes.
   */
  const restoreDropdownValue = useCallback(() => {
    if (!isDirty) return;

    setDropdownValue(getDropdownValue(value));
  }, [isDirty, value]);

  /**
   * Safely (re-)initialize the custom date inputs using either the last emitted
   * value, or the value prop if defined. Discards dirty state to provide a
   * clean slate.
   *
   * Stored custom-range values carry the user's original calendar day (e.g.
   * `2026-04-23T00:00:00+01:00`). Reconstruct Dates from the date portion so
   * the picker re-renders the same day regardless of the stored offset —
   * otherwise east-of-UTC users see the day shift backwards on reopen.
   */
  const restoreInputValues = useCallback(() => {
    if (!value) return;

    if (isCustomDateRange(value)) {
      setStartDate(parseLocalCalendarDate(value[0]));
      setEndDate(parseLocalCalendarDate(value[1]));
    } else {
      const [start, end] = getRelativeDateRange(value);

      setStartDate(start);
      setEndDate(end);
    }

    setIsDirty(false);
  }, [value]);

  useEffect(() => {
    setValue(initialValue);
    setDropdownValue(getDropdownValue(initialValue));
  }, [initialValue]);

  useEffect(restoreInputValues, []);

  /**
   * Updates state when a custom range is changed via the calendar directly. The
   * formatted date values are reflected back to the inputs.
   */
  const handleRangeChange = useCallback((dates: DateRangeRawValues) => {
    const [start, end] = dates;

    setStartDate(start);
    setEndDate(end);
    setIsDirty(true);

    formatStartDateInput(start);
    formatEndDateInput(end);
  }, []);

  /**
   * Emits a new custom range and closes the dropdown.
   */
  const handleApplyCustomRange = useCallback(() => {
    const newValue: DateRangeValue = [
      startDate ? formatISO(startDate) : '',
      endDate ? formatISO(endDate) : null,
    ];

    const rawValues: DateRangeRawValues = [startDate, endDate];

    onChange(newValue, rawValues);

    setValue(newValue);
    setIsOpen(false);
  }, [onChange, endDate, startDate]);

  const rangePicker = useMemo(
    () => (
      <div className={s.rangePicker}>
        <div className={s.rangePickerInputs}>
          <Input
            ref={startDateInputRef}
            aria-label="Start Date"
            id="startDate"
            name="startDate"
            size="mini"
            icon="calendar"
            iconPosition="left"
            defaultValue={formatDate(startDate)}
            onChange={(_, data) => {
              const parsedDate = new Date(data.value);

              if (Number.isNaN(parsedDate.valueOf())) return;

              setStartDate(parsedDate);
              setEndDate(null);
              setIsDirty(true);
            }}
            onBlur={() => {
              formatStartDateInput(startDate);
            }}
          />

          <span>&nbsp;&mdash;&nbsp;</span>

          <Input
            ref={endDateInputRef}
            aria-label="End Date"
            id="endDate"
            name="endDate"
            size="mini"
            icon="calendar"
            iconPosition="left"
            defaultValue={formatDate(endDate)}
            onChange={(_, data) => {
              const parsedDate = new Date(data.value);

              if (Number.isNaN(parsedDate.valueOf())) return;

              setEndDate(parsedDate);
              setIsDirty(true);
            }}
            onBlur={() => {
              formatEndDateInput(endDate);
            }}
          />
        </div>

        <div className={s.calendar}>
          <ReactDatePicker
            className={datePickerClassName}
            onChange={handleRangeChange}
            // Ensures the calendar reflects manual changes from the date inputs
            openToDate={endDate ?? startDate ?? undefined}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            fixedHeight
            inline
          />
        </div>

        <div className={s.rangePickerActions}>
          <Button
            content="Cancel"
            onClick={event => {
              // Prevent this click event bubbling to the dropdown itself
              event.stopPropagation();

              restoreDropdownValue();
              setIsOpen(false);
            }}
            compact
            basic
          />

          <Button
            content="Apply"
            onClick={handleApplyCustomRange}
            disabled={!isDirty}
            primary
            compact
          />
        </div>
      </div>
    ),
    [
      startDate,
      endDate,
      datePickerClassName,
      handleRangeChange,
      handleApplyCustomRange,
      restoreDropdownValue,
      isDirty,
    ],
  );

  const handleItemClick = useCallback(
    (_: SyntheticEvent, data: DropdownItemProps) => {
      const newValue = data.value as DateRangePresetValue;

      // Only continue if the value has actually changed
      if (newValue === dropdownValue) return;

      setIsDirty(true);
      setDropdownValue(newValue);

      if (newValue === 'custom') {
        setTimeout(() => startDateInputRef.current?.focus());
      } else {
        const rawValues: DateRangeRawValues = getRelativeDateRange(newValue);

        onChange(newValue, rawValues);

        setValue(newValue);
        setIsOpen(false);
      }
    },
    [onChange, dropdownValue],
  );

  const presetOptions = useMemo(
    () =>
      Object.entries(dateRangePresets).map(([key, preset]) => {
        const isActive = key === dropdownValue;
        const isCustom = key === 'custom';

        return (
          <Dropdown.Item
            aria-label={preset.label}
            className={classNames(
              s.dropdownItem,
              isCustom && s.customRangeOption,
            )}
            key={key}
            value={key}
            active={isActive}
            onClick={handleItemClick}
          >
            {isCustom && isActive && isOpen ? (
              <div className={s.customRangeContent}>
                <span>{preset.label}</span>
                {rangePicker}
              </div>
            ) : (
              <span>{preset.label}</span>
            )}
          </Dropdown.Item>
        );
      }),
    [dropdownValue, handleItemClick, rangePicker, isOpen],
  );

  // Rebind an Escape listener since we need to control all dropdown state here
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;

    restoreDropdownValue();
    setIsOpen(false);
  };

  // Close and reset the dropdown when clicking outside of it
  useOnClickOutside(dropdownMenuRef, () => {
    if (!isOpen) return;

    restoreDropdownValue();
    setIsOpen(false);
  });

  const displayText = useMemo(() => {
    if (dropdownValue) {
      if (dropdownValue === 'custom' && startDate) {
        if (!endDate) {
          return `Since ${formatDate(startDate)}`;
        }
        return `${formatDate(startDate)} – ${formatDate(endDate)}`;
      }
      return dateRangePresets[dropdownValue].label;
    }
  }, [dropdownValue, startDate, endDate]);

  return (
    <Dropdown
      className={classNames(s.dropdown, className)}
      aria-label="Date Range"
      open={isOpen}
      onOpen={() => {
        if (isOpen) return;

        setIsOpen(true);
        restoreInputValues();
      }}
      onKeyDown={handleKeyDown}
      value={dropdownValue}
      text={displayText}
      placeholder="Select date range"
      disabled={disabled}
      button
      basic
      floating
      {...dropdownProps}
    >
      <Dropdown.Menu
        ref={dropdownMenuRef}
        className={classNames(
          s.dropdownMenu,
          dropdownValue === 'custom' && s.customRangeIsActive,
        )}
        tabIndex={0}
      >
        {presetOptions}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default DateRange;
